import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from "react-helmet";
import SiteHeader from '../components/SiteHeader';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';

// --- CONSTANTS: กำหนดหัวข้อสารบัญ ---
const SECTIONS = [
  { id: "abstract", title: "บทคัดย่อ" },
  { id: "history-origin", title: "๑. หอสมุดแห่งชาติสาขาเชียงใหม่ (จุดเริ่มต้น)" },
  { id: "history-current", title: "๒. หอสมุดแห่งชาติรัชมังคลาภิเษก (ปัจจุบัน)" },
  { id: "criteria", title: "หลักเกณฑ์การคัดเลือก" },
  { id: "type-1", title: "ประเภทที่ ๑: ตราหอพระสมุดวชิรญาณ" },
  { id: "type-2", title: "ประเภทที่ ๒: ประวัติการครอบครอง" },
  { id: "type-3", title: "ประเภทที่ ๓: ลักษณะโดดเด่น" },
  { id: "type-4", title: "ประเภทที่ ๔: โรงพิมพ์ยุคแรก" },
  { id: "type-5", title: "ประเภทที่ ๕: พิมพ์ครั้งแรก" },
  { id: "type-6", title: "ประเภทที่ ๖: บุ๊คเพลท" },
  { id: "type-7", title: "ประเภทที่ ๗: โอกาสพิเศษ" },
  { id: "type-8", title: "ประเภทที่ ๘: รูปเล่มสวยงาม" },
  { id: "type-9", title: "ประเภทที่ ๙: ภาพเก่า" },
  { id: "type-10", title: "ประเภทที่ ๑๐: ชีวประวัติ" },
  { id: "type-11", title: "ประเภทที่ ๑๑: ลายพระราชหัตถเลขา" },
  { id: "type-12", title: "ประเภทที่ ๑๒: ภาษาต่าง ๆ" },
  { id: "conclusion", title: "บทสรุป" },
  { id: "bibliography", title: "บรรณานุกรม" },
];

// --- HELPER COMPONENTS ---

// 1. แถบความคืบหน้าการอ่าน (Reading Progress Bar)
function ReadingProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const progress = (h.scrollTop / (h.scrollHeight - h.clientHeight)) * 100;
      setPct(progress || 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div className="fixed top-0 left-0 right-0 z-[55] h-1 bg-black/5">
      <div
        className="h-1 bg-orange-600 transition-[width] duration-150"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// 2. Dropdown สารบัญสำหรับ Mobile
function HeaderTOC({ sections }) {
  const onChange = (e) => {
    const id = e.target.value;
    if (!id) return;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <div className="w-full max-w-[480px]">
      <div className="relative">
        <select
          onChange={onChange}
          className="w-full appearance-none px-3 sm:px-4 py-2.5 rounded-xl border border-amber-200 bg-white/90 text-orange-900 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30"
        >
          <option value="">สารบัญ...</option>
          {sections.map((s) => (
            <option key={s.id} value={s.id}>{s.title}</option>
          ))}
        </select>
        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-orange-900/60">▾</div>
      </div>
    </div>
  );
}

// 3. ปุ่มนำทาง ถัดไป/ก่อนหน้า ใน Sidebar
function PrevNext({ sections, active }) {
  const idx = sections.findIndex((s) => s.id === active);
  const prev = idx > 0 ? sections[idx - 1] : null;
  const next = idx < sections.length - 1 ? sections[idx + 1] : null;
  return (
    <div className="not-prose mt-8 flex flex-wrap gap-3">
      {prev && (
        <a href={`#${prev.id}`} className="px-4 py-2 rounded-xl border border-amber-200 bg-white text-orange-900 hover:border-orange-500 hover:text-orange-600 transition text-sm">
          ← {prev.title}
        </a>
      )}
      {next && (
        <a href={`#${next.id}`} className="px-4 py-2 rounded-xl border border-amber-200 bg-white text-orange-900 hover:border-orange-500 hover:text-orange-600 transition text-sm">
          {next.title} →
        </a>
      )}
    </div>
  );
}

// 4. Component รูปภาพแบบมีการ์ด (Image Card)
const ImageCard = ({ src, alt, captionTitle, captionDetail }) => (
  <figure className="flex flex-col w-full md:w-[30%] mb-6 md:mb-0 rounded-lg shadow-sm border border-amber-100 overflow-hidden bg-white h-full">
    <div className="w-full h-64 bg-gray-50 flex items-center justify-center p-2">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="max-h-full max-w-full object-contain rounded"
      />
    </div>
    <figcaption className="p-4 text-center text-gray-700 text-sm flex-grow flex flex-col justify-start">
      <strong className="block mb-1 text-orange-800">{captionTitle}</strong>
      {captionDetail && <span className="block text-gray-600 mt-1">{captionDetail}</span>}
    </figcaption>
  </figure>
);

// --- MAIN COMPONENT ---
const RareBooksArticlePage = () => {
  const [active, setActive] = useState("abstract");
  const containerRef = useRef(null);

  // SEO: Structured Data (JSON-LD)
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "“หนังสือหายาก ๑ ศตวรรษ” ขุมทรัพย์ทางปัญญาแห่งล้านนา",
    "image": [
      "https://ebookcnx.com/assets/โรงช้างต้น.jpg" // ควรเปลี่ยนเป็น URL จริงของเว็บ
    ],
    "author": {
      "@type": "Person",
      "name": "สุคนธ์ทิพย์ จันทะลุน"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Culture Reader @NL.CNX",
      "logo": {
        "@type": "ImageObject",
        "url": "https://ebookcnx.com/assets/logo.webp" // ควรเปลี่ยนเป็น URL จริง
      }
    },
    "datePublished": "2025-01-01", // ปรับวันที่เผยแพร่จริง
    "description": "บทความนำเสนอหนังสือหายากที่มีการจัดพิมพ์ก่อนปี พ.ศ. ๒๔๖๗ ของหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่"
  };

  useEffect(() => {
    if (window.snapSaveState) {
      window.snapSaveState = () => { };
    }
    const timer = setTimeout(() => {
      if (window.snapSaveState) {
        window.snapSaveState = null;
      }
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target?.id) setActive(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: [0, 0.1] }
    );
    const nodes = containerRef.current?.querySelectorAll("section[id], div[id]") ?? [];
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, []);

  const Anchor = ({ id, children }) => (
    <a
      href={`#${id}`}
      className={`block py-1.5 text-sm transition-colors duration-200 pl-3 border-l-2 ${active === id
        ? "text-orange-700 font-medium border-orange-600"
        : "text-gray-600 hover:text-orange-600 border-transparent"
        }`}
    >
      {children}
    </a>
  );

  return (
    <div className="min-h-screen bg-[#f8f6f3]">
      {/* ===== Helmet (SEO) ===== */}
      <Helmet>
        <title>“หนังสือหายาก ๑ ศตวรรษ” · Digital Library</title>
        <meta
          name="description"
          content="บทความโดย สุคนธ์ทิพย์ จันทะลุน นำเสนอหนังสือหายากที่มีการจัดพิมพ์ก่อนปี พ.ศ. ๒๔๖๗ ของหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่ ขุมทรัพย์ทางปัญญาแห่งล้านนา"
        />
        <meta property="og:title" content="“หนังสือหายาก ๑ ศตวรรษ” ขุมทรัพย์ทางปัญญาแห่งล้านนา" />
        <meta
          property="og:description"
          content="แหล่งรวบรวมหนังสือหายากในท้องถิ่น ตั้งแต่อดีตจนถึงปัจจุบัน ณ หอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่"
        />
        <meta property="og:image" content="/assets/โรงช้างต้น.jpg" />
        <meta property="og:type" content="article" />
        {/* Inject JSON-LD Schema */}
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      <ReadingProgress />
      <SiteHeader centerSlot={<HeaderTOC sections={SECTIONS} />} />

      <header className="relative bg-white pt-16 pb-12 text-center border-b border-gray-200 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            “หนังสือหายาก ๑ ศตวรรษ” <br className="hidden md:inline" />
            ขุมทรัพย์ทางปัญญาแห่งล้านนา
          </h1>
          <p className="text-gray-500 italic text-lg">
            โดย สุคนธ์ทิพย์ จันทะลุน
          </p>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-12 gap-10">

          <main className="lg:col-span-8">
            <article ref={containerRef} className="prose prose-lg max-w-none text-gray-700">

              {/* ID: abstract */}
              <section id="abstract" className="scroll-mt-28">
                <h2 className="text-2xl font-bold text-orange-800 mt-0 mb-6 pb-2 border-b border-gray-200">
                  บทคัดย่อ
                </h2>
                <p className="mb-6 leading-relaxed indent-10">
                  บทความนี้ มีวัตถุประสงค์เพื่อนําเสนอ หนังสือหายากที่มีการจัดพิมพ์ก่อนปี พ.ศ. ๒๔๖๗ ของหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่ ซึ่งนับว่าเป็นทรัพยากรสารสนเทศที่มีความสําคัญทางวิชาการทั้ง ด้านประวัติและพัฒนาการของการพิมพ์ ตัวอักษร ภาษา รวมถึงประวัติศาสตร์ สังคม วัฒนธรรมของ แต่ละท้องถิ่นในแต่ละยุคแต่ละสมัย โดยใช้เป็นเอกสารอ้างอิงสําหรับการศึกษา ค้นคว้า และวิจัยนอกจากนี้ ยังอธิบายถึงความเป็นมาและพัฒนาการของหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่ ในฐานะเป็นแหล่งรวบรวมหนังสือหายากในท้องถิ่น ตั้งแต่อดีตจนถึงปัจจุบัน ซึ่งถือเป็นแหล่งเรียนรู้หนังสือหายากที่สําคัญประจําภาคเหนือ
                </p>
                <div className="bg-orange-50 border-l-4 border-orange-600 p-6 my-8 rounded-r-lg">
                  <blockquote className="text-lg italic text-gray-700 mb-4 border-none p-0">
                    “หนังสือหายากนั้น เป็นสิ่งที่มีคุณค่าทางภูมิปัญญาของชาติ แต่มีสภาพบอบบางและชำรุด ขอให้หอสมุดแห่งชาติอนุรักษ์ให้มีสภาพดี จัดทำสำเนาเพื่อรักษาต้นฉบับ แล้วเผยแพร่ภูมิปัญญาบรรพบุรุษไทยให้แพร่หลายต่อไป”
                  </blockquote>
                  <footer className="text-sm text-gray-600 font-medium">
                    — พระราชดํารัสสมเด็จพระเทพรัตนราชสุดาฯ สยามบรมราชกุมารี พระราชทานเมื่อครั้งทรงเสด็จทอดพระเนตรหนังสือหายาก หอสมุดแห่งชาติ เมื่อวันที่ ๓๐ กันยายน พุทธศักราช ๒๕๓๕
                  </footer>
                </div>
                <p className="mb-6 text-lg leading-relaxed">
                  สะท้อนให้เห็นว่า “หนังสือหายาก” มีคุณค่าและมีความสําคัญในฐานะมรดกภูมิปัญญาของชาติที่บรรพบุรุษในอดีตได้ บันทึกเรื่องราว ความรู้ ความคิด ประสบการณ์ เป็นเอกสารหลักฐานที่มีความสําคัญด้านประวัติและพัฒนาการของการพิมพ์ ตัวอักษรและภาษา รวมถึงประวัติศาสตร์ สังคม วัฒนธรรมของแต่ละท้องถิ่นในแต่ละยุคแต่ละสมัย และถ่ายทอดสืบต่อกันมาจากอดีตจนถึงปัจจุบัน เป็นแหล่งข้อมูลสําคัญทางวิชาการตลอดจนได้ทราบถึงข้อมูลอันเป็นประโยชน์ในสาขาวิทยาการต่าง ๆ โดย ใช้เป็นเอกสารอ้างอิงสําหรับการ ศึกษา ค้นคว้า และวิจัย ด้วยความสําคัญและคุณค่าของหนังสือหายากจึงเปรียบเสมือนเป็นมรดกภูมิปัญญาของชาติที่ควรค่าแก่การอนุรักษ์ไว้มิให้สูญหายไปตามกาลเวลา โดยสถาบันสารสนเทศที่ถือเป็นเสาหลักของชาติในการรวบรวม จัดเก็บ และอนุรักษ์หนังสือหายากในประเทศไทย คือ หอสมุดแห่งชาติ (National Library)
                </p>
              </section>

              {/* ID: history-origin */}
              <section id="history-origin" className="scroll-mt-28">
                <h2 className="text-2xl font-bold text-orange-800 mt-12 mb-6 pb-2 border-b border-gray-200 flex items-center gap-2">
                  หอสมุดแห่งชาติสาขาเชียงใหม่ จุดเริ่มต้น “แหล่งรวบรวมหนังสือหายาก” ในท้องถิ่น
                </h2>
                <figure className="w-full md:w-5/12 md:float-left md:mr-8 mb-6">
                  <img
                    src="/assets/โรงช้างต้น.jpg"
                    alt="โรงช้างต้น อดีตที่ตั้งหอสมุดมณฑลพายัพ"
                    loading="lazy"
                    className="w-full h-auto rounded-lg shadow-md object-cover"
                  />
                  <figcaption className="text-center text-sm text-gray-500 mt-2">
                    ภาพที่ ๑ โรงช้างต้นที่ได้จัดตั้งเป็นหอสมุดกลางมณฑลพายัพ (หอสมุดแห่งชาติสาขาเชียงใหม่)
                  </figcaption>
                </figure>
                <p className="mb-6 leading-relaxed indent-10">
                  แรกเริ่ม“หอสมุดแห่งชาติสาขาเชียงใหม่” เกิดขึ้นจากคณะกรรมการพิพิธภัณฑ์สถานแลหอสมุดมณฑลพายัพ ได้เฝ้าพระราชชายาเจ้าดารารัศมี เมื่อวันที่ ๑ เมษายน พ.ศ. ๒๔๗๑ องค์อุปถัมภิกาพิพิธภัณฑ์สถานแลหอสมุดมณฑลพายัพ ได้ประทานคําแนะนําเรื่องสถานที่ที่จะตั้งหอสมุดซึ่งคณะกรรมการฯ จะจัดให้เป็น “สาขาของหอพระสมุดสําหรับพระนคร” และเมื่อคณะกรรมการฯ ได้ตกลงกันเลือกตามพระราชดําริแล้ว จึงได้เริ่มการก่อสร้างหอสมุดกลางมณฑลพายัพ ซึ่งในระหว่างที่ก่อสร้างอาคารหอสมุดกลางมณฑลพายัพ ได้รับการสนับสนุนจากราชบัณฑิตยสภาให้จัดตั้งหอสมุดกลางมณฑลพายัพขึ้นเป็นการชั่วคราวที่โรงช้างต้น ต่อมาในปี พ.ศ.๒๔๗๖ การสร้างหอสมุดกลางมณฑลพายัพยังไม่แล้วเสร็จเนื่องจากขาดแคลนเงินในการสร้างต่อให้สําเร็จ หลวงวิจิตรวาทการ อธิบดีกรมศิลปากรในสมัยนั้นจึงได้จัดสรรงบประมาณสนับสนุนการก่อสร้างหอสมุดฯ นี้ จนแล้วเสร็จและได้จัดเป็น“หอสมุดแห่งชาติสาขาเชียงใหม่”
                </p>
                <p className="mb-6 leading-relaxed indent-10">
                  ในปี พ.ศ.๒๔๗๘ โรงเรียนยุพราชวิทยาลัย ประสบปัญหาสถานที่เรียนไม่พอกับจํานวนนักเรียน ข้าหลวงตรวจการแผนกธรรมการเชียงใหม่ จึงได้ขอยืมหอสมุดของกรมศิลปากรเป็นห้องสมุดชั่วคราวของโรงเรียนยุพราชวิทยาลัย โดยกรมศิลปากรได้มีความเห็นว่า “...หอสมุดหลังนี้ ทางฝ่ายบ้านเมืองเป็นผู้เรี่ยไรก่อสร้างขึ้นโดยแสดงความจํานงจะให้เป็นหอสมุดกลางจังหวัดเชียงใหม่ ฉะนั้นสิทธิ์ที่จะอนุญาตให้โอนจึงไม่ได้อยู่ที่กรมศิลปากร เวลานี้กรมศิลปากรได้ทําความตกลงกับข้าหลวงประจําจังหวัดเชียงใหม่ เปิดสาขาหอสมุดแห่งชาติประจําเชียงใหม่ขึ้นแล้ว... เมื่อสาขาหอสมุดแห่งชาติจําเป็นต้องย้ายไปอยู่ที่อื่นแล้ว กรมศิลปากรก็ไม่ขัดข้องขอมอบให้อยู่ในดุลยพินิจของคณะกรรมการจังหวัดเชียงใหม่...”
                </p>
                <figure className="w-full md:w-5/12 md:float-left md:mr-8 mb-6">
                  <img
                    src="/assets/อาคารหอสมุดแห่งชาติสาขาเชียงใหม่(เดิม).jpg"
                    alt="อาคารหอสมุดแห่งชาติสาขาเชียงใหม่ (เดิม) ในปัจจุบัน"
                    loading="lazy"
                    className="w-auto h-auto rounded-lg shadow-md object-cover"
                  />
                  <figcaption className="text-center text-sm text-gray-500 mt-2">
                    ภาพที่ ๒ อาคาหอสมุดแห่งชาติสาขาเชียงใหม่ (เดิม) ในปัจจุบันเป็นอาคารเรียนวิชาดนตรีของโรงเรียนยุพราชวิทยาลัย“หอสมุดแห่งชาติสาขา
                  </figcaption>
                </figure>
                <p className="mb-6 leading-relaxed">
                  ต่อมาในปี พ.ศ.๒๕๑๑ กรมวิสามัญศึกษาได้ขอโอนที่ดินที่ตั้งหอสมุดจังหวัดเชียงใหม่ และขอรวมกิจการของหอสมุดฯเข้ากับห้องสมุดโรงเรียนยุพราชวิทยาลัย หากแต่กรมศิลปากรตกลงโอนให้เฉพาะที่ดิน ส่วนกิจการของหอสมุดฯจําต้องสงวนไว้ เพราะกรมศิลปากรกําลังดําเนินการเพื่อจัดสร้างหอสมุดแห่งชาติประจําจังหวัดเชียงใหม่ขึ้นในที่ดินของกรมศิลปากร หากจะรื้อถอนอาคารหอสมุดก็ขอให้รวบรวมเอกสารหนังสือของหอสมุดฯทั้งหมดไปเก็บรักษาไว้ในที่ใดที่หนึ่งอย่างเป็นสัดส่วน โดยปลอดภัยจากการชํารุดสูญหาย เมื่อกรมศิลปากรสร้างหอสมุดฯเสร็จแล้วจะขอรับคืนมา ซึ่งต่อมากรมศิลปากรได้โอนกรรมสิทธิ์ในที่ดินที่ตั้งหอสมุดให้เป็นสมบัติของโรงเรียนยุพราชวิทยาลัยแล้ว “หอสมุดแห่งชาติสาขาเชียงใหม่” ก็เปิดให้บริการต่อมาอีกระยะหนึ่งจนถึงปี พ.ศ. ๒๕๑๘ ต้องปิดให้บริการลงโดยได้ขนย้ายหนังสือ ครุภัณฑ์และวัสดุห้องสมุดไปจัดเก็บไว้ที่หอสมุดแห่งชาติสาขาลําพูน ซึ่งเป็นสาขาที่ย้ายมาจากหอสมุดแห่งชาติสาขาเชียงใหม่
                </p>
              </section>

              {/* ID: history-current */}
              <section id="history-current" className="scroll-mt-28">
                <h2 className="text-2xl font-bold text-orange-800 mt-12 mb-6 pb-2 border-b border-gray-200 flex items-center gap-2">
                  หอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่ “แหล่งเรียนรู้  หนังสือหายาก” ประจําภาคเหนือ
                </h2>
                <p className="mb-6 leading-relaxed indent-10">
                  หอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่ (Ratchamangkhalaphisek National Library of Chiang Mai) เกิดจากดําริของ ฯพณฯ พลเอกเปรม ติณสูลานนท์ องคมนตรีและรัฐบุรุษ ที่จะให้ดําเนินการก่อสร้างหอสมุดแห่งชาติประจําภาคเหนือ เพื่อถวายเป็นราชสดุดีแด่อง ค์พระบาทสมเด็จพระเจ้าอยู่หัวภูมิพลอดุลยเดชฯ เนื่องในวโรกาสมหามงคลเฉลิมฉลองปีรัชมังคลาภิเษก ๒๕๓๑๗ สมเด็จพระเทพรัตนราชสุดาฯ สยามบรมราชกุมารี เสด็จเป็นองค์ประธานในพิธีเปิดหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่   เมื่อวันที่ ๓๑ สิงหาคม พ.ศ. ๒๕๓๒นับว่าเป็น “หอสมุดแห่งชาติส่วนภูมิภาคแห่งเดียวที่ตั้งอยู่ประจําภาคเหนือ” จัดตั้งขึ้นเพื่อเป็นแหล่งเรียนรู้ ทําหน้าที่รวบ รวม  จัดเก็บ บริการ เผยแพร่ และอนุรักษ์ทรัพยากรสารสนเทศอันเป็นมรดกภูมิปัญญาของชาติ เพื่อรักษาไว้เป็นหลักฐานทางประวัติศาสตร์และเผยแพร่ความรู้ที่เป็นภูมิปัญญาของบรรพบุรุษใน   ท้องถิ่นและประเทศชาติแก่สาธารณชนทั่วไป โดยมีภารกิจสําคัญประการหนึ่ง  ตามหลักสากล   ซึ่ง ได้กําหนด หน้าที่ของหอสมุดแห่งชาติที่เกี่ยวข้องกับหนังสือหายากไว้ประการหนึ่งว่า “หอสมุดแห่งชาติเป็นแหล่งรวบรวมสิ่งตีพิมพ์ และสื่อความรู้ทุกชนิดในชาติให้สมบูรณ์ที่สุด โดยเน้นต้นฉบับหนังสือตัวเขียน (Manuscript)และหนังสือเก่า (Rare Book)
                </p>
                <figure className="w-full md:w-5/12 md:float-left md:mr-8 mb-6">
                  <img
                    src="/assets/หอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่.jpg"
                    alt="หอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่"
                    loading="lazy"
                    className="w-full h-auto rounded-lg shadow-md object-cover"
                  />
                  <figcaption className="text-center text-sm text-gray-500 mt-2">
                    ภาพที่ ๓ หอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่
                  </figcaption>
                </figure>
                <p className="mb-6 leading-relaxed indent-10">
                  ปัจจุบันหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่ สังกัดสํานักศิลปากรที่ ๗ เชียงใหม่ กรมศิลปากร กระทรวงวัฒนธรรม เป็นแหล่งรวบรวม จัดเก็บหนังสือหายากที่สําคัญแห่งหนึ่งในท้องถิ่นและประเทศไทยการรวบรวมหนังสือหายากของหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่นั้น  เริ่มตั้งแต่  มีการก่อตั้งหอสมุดฯ ขึ้นเมื่อปี พ.ศ. ๒๕๓๒ โดยการรวบรวมหนังสือเก่าหาได้ยากทั้งที่ได้รับมอบจากหน่วยงาน บุคคลต่าง ๆ และหนังสือที่ได้ขนย้ายจากหอสมุดแห่งชาติสาขาจังหวัดเชียงใหม่ (หอสมุดกลาง มณฑลพายัพ     หรือห้องสมุดโรงเรียนประจํามณฑลพายัพ “ยุพราชวิทยาลัย”) นําไปจัดเก็บไว้ที่หอสมุดแห่งชาติสาขาลําพูน จนกระทั่งในปี พ.ศ. ๒๕๕๓ กรมศิลปากรได้ดําเนินการถ่ายโอนภารกิจของหอสมุดแห่งชาติสาขาลําพูนให้แก่เทศบาลเมืองลําพูน   มีการลงนามส่งมอบ เมื่อวันที่ ๙ กรกฎาคม พ.ศ. ๒๕๕๓ซึ่งได้เปลี่ยนชื่อเป็น หอสมุดแห่งชาติหริภุญไชย๙ จึงได้ขนย้ายหนังสือบางส่วนที่พิจารณาคัดเลือกแล้วว่าเป็นหนังสือหายากมาดําเนินการรวบรวมและจัดเก็บให้บริการที่หอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่
                </p>
                <p className="mb-6 leading-relaxed indent-10">
                  หนังสือหายากของหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่ ที่ได้ดําเนินการตามหลักวิชาการทางบรรณารักษศาสตร์และสารสนเทศศาสตร์เรียบร้อยแล้ว โดยรวบรวม จัดเก็บ ให้บริการและอนุรักษ์มีจํานวน ทั้งหมด ๒,๙๓๒ ชื่อเรื่อง
                </p>
                <figure className="w-full md:w-5/12 md:float-left md:mr-8 mb-6">
                  <img
                    src="/assets/ห้องภาษาโบราณ.jpg"
                    alt="ห้องภาษาโบราณที่จัดเก็บหนังสือหายากของหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่"
                    loading="lazy"
                    className="w-full h-auto rounded-lg shadow-md object-cover"
                  />
                  <figcaption className="text-center text-sm text-gray-500 mt-2">
                    ภาพที่ ๔ ห้องภาษาโบราณที่จัดเก็บหนังสือหายากของหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่
                  </figcaption>
                </figure>
                <p className="mb-6 leading-relaxed">
                  ประกอบด้วย ภาษาไทย จํานวน ๒,๔๐๗ ชื่อเรื่อง ภาษาต่างประเทศ จํานวน ๔๕๙ ชื่อเรื่อง และภาษาถิ่น ( ล้านนา) จํานวน ๖๖ ชื่อเรื่อง อาทิ พระราชนิพนธ์ พระนิพนธ์ หนังสือส่วนพระองค์ พระราชหัตถเลขา จดหมายเหตุ ตํานาน  พงศาวดาร หนังสือแบบเรียน หนังสือที่จัดพิมพ์ในโอกาสต่างๆ หนังสือกรมศิลปากร หนังสือชีวประวัติบุคคลสําคัญในท้องถิ่น หนังสือฉบับพิมพ์ครั้งแรก หนังสือภาพหายาก หนังสือที่พิมพ์ในโรงพิมพ์ยุคแรก หนังสือที่มีความโดดเด่นมีรูปเล่มสวยงาม และมีภาพประกอบที่ทรงคุณค่า เป็นต้น
                </p>
              </section>

              {/* ID: criteria */}
              <section id="criteria" className="scroll-mt-28">
                <h2 className="text-2xl font-bold text-orange-800 mt-12 mb-6 pb-2 border-b border-gray-200 flex items-center gap-2">
                  “หลักเกณฑ์การคัดเลือก” หนังสือหายากของหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่
                </h2>
                <p className="mb-6 leading-relaxed indent-10">
                  หนังสือหายากของหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่ เป็นหนังสือที่ผ่านการคัดเลือกตามหลักเกณฑ์ที่ได้กําหนดในการพิจารณาดําเนินการประเมินคุณค่าตามข้อสังเกตลักษณะของหนังสือหายากของสํานักหอสมุดแห่งชาติ ซึ่งนํามาใช้เป็นแนวทางในการคัดเลือกหนังสือหายากของหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่๑๐ ดังนี้
                </p>
                <ul className="bg-white rounded-xl p-6 space-y-3 list-none border border-amber-100 shadow-sm">
                  <li className="flex gap-3 items-start">
                    <span className="text-orange-500 font-bold mt-1">•</span>
                    <span><strong className="text-gray-900">๑. หนังสือเก่าแก่หาได้ยาก (Rarity):</strong> เป็นหนังสือที่มีคุณค่าในด้านการพิมพ์ เป็นเอกสารปฐมภูมิ(Primary source) ในด้านประวัติศาสตร์และพัฒนาการพิมพ์ของโลก ส่วนใหญ่เป็นหนังสือที่พิมพ์ในรุ่นแรก ๆ ของโลก หนังสือที่พิมพ์ในยุคก่อนที่มนุษย์จะคิดค้นวิธีเรียงพิมพ์ และหนังสือที่ผลิตขึ้นด้วยวิธีเรียงพิมพ์</span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <span className="text-orange-500 font-bold mt-1">•</span>
                    <span><strong className="text-gray-900">๒. หนังสือซึ่งมีความสําคัญในด้านที่เป็นเอกสารปฐมภูมิในด้านประวัติศาสตร์การพิมพ์ของแต่ละประเทศหรือแต่ละภูมิภาค (Local rarity):</strong> เป็นหนังสือที่จัดพิมพ์ในช่วงระยะเวลา ๕๐ - ๘๐ ปีแรกของพัฒนาการด้านการพิมพ์ในประเทศไทย กิจการด้านการพิมพ์ปรากฏหลักฐานเริ่มเมื่อปี พ.ศ. ๒๓๗๙ที่กล่าวถึงสิ่งพิมพ์ภาษาไทยฉบับแรกที่พิมพ์ขึ้นในเมืองไทย”๑๑ดังนั้นหนังสือที่จัดพิมพ์อยู่ระหว่างปีพ.ศ. ๒๓๗๙  – ๒๔๖๐ จึงเป็นหนังสือซึ่งมีความสําคัญในด้านประวัติศาสตร์การพิมพ์ของประเทศไทย</span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <span className="text-orange-500 font-bold mt-1">•</span>
                    <span><strong className="text-gray-900">๓. หนังสือที่มีการพิมพ์จํานวนจํากัด (Limited edition):</strong> เป็นหนังสือที่มีการจัดพิมพ์จํานวนไม่มากในการพิมพ์ครั้งหนึ่ง ส่วนใหญ่จะเป็นหนังสือที่จัดพิมพ์ในโอกาสพิเศษหรือด้วยวัตถุประสงค์เฉพาะอย่างใดอย่างหนึ่ง เพื่อเป็นอภินันทนาการแก่กลุ่มบุคคล จึงมีการพิมพ์จํานวนจํากัด</span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <span className="text-orange-500 font-bold mt-1">•</span>
                    <span><strong className="text-gray-900">๔. หนังสือฉบับพิมพ์ครั้งแรก (First edition):</strong> เป็นงานเขียนที่ได้รับการจัดพิมพ์ต้นฉบับออกมาในรูปเล่มของหนังสือเป็นครั้งแรก โดยงานที่เป็นที่นิยมอาจได้รับการตีพิมพ์หรือตีพิมพ์ซ้ําโดยผู้จัดพิมพ์หรือสํานักพิมพ์หลายรายและในรูปแบบต่าง ๆ</span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <span className="text-orange-500 font-bold mt-1">•</span>
                    <span><strong className="text-gray-900">๕. หนังสือที่มีเนื้อหาเป็นที่กล่าวขวัญถึงหรือความเด่นของหนังสือ (Reputation):</strong> เป็นหนังสือที่ได้รับการตีพิมพ์และมีความเด่นในเนื้อหาสาระหรือการใช้ภาษาอย่างถูกต้องไพเราะ ผลงานส่วนน้อยที่แตกต่างจากผลงานส่วนใหญ่ เรื่องที่กุขึ้นหรือเรื่องที่เป็นการปลอมแปลงวรรณกรรม</span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <span className="text-orange-500 font-bold mt-1">•</span>
                    <span><strong className="text-gray-900">๖. หนังสือเก่าที่มีสภาพรูปเล่มสมบูรณ์ หรือสภาพรูปเล่มที่สวยงาม:</strong> เป็นหนังสือเก่าที่ตกทอดมาจนถึงปัจจุ   บันที่มีสภาพเหมือนแรกออกจําหน่าย เช่น หนังสือที่ใช้วัสดุมีค่าจัดทําอย่างประณีต สวยงาม หรือการเย็บเล่มเข้าเล่มทําเป็นพิเศษ เป็นต้น</span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <span className="text-orange-500 font-bold mt-1">•</span>
                    <span><strong className="text-gray-900">๗. หนังสือที่มีประวัติการครอบครองว่าเคยเป็นของผู้ใดมาก่อน:</strong> เป็นหนังสือที่มีหลักฐานหรือประวัติการครอบครอง เช่น หนังสือของพระเจ้าแผ่นดิน ผู้นําประเทศ หรือบุคคลสําคัญของโลก</span>
                  </li>
                </ul>
              </section>

              {/* Section 3 Highlights */}
              <section id="rare-books-intro" className="scroll-mt-28">
                <h2 className="text-2xl font-bold text-orange-800 mt-12 mb-6 pb-2 border-b border-gray-200 flex items-center gap-2">
                  “หนังสือหายากที่มีอายุ ๑๐๐ ปี” ของหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่
                </h2>
                <p className="mb-6 indent-10">
                  หนังสือหายาก เป็นทรัพยากรสารสนเทศที่มีคุณค่าถือเป็นมรดกทรัพย์สินทางปัญญาของชาติทั้งด้านเนื้อหาและด้านการพิมพ์ มีอายุประมาณ ๕๐ – ๑๕๐ ปี ซึ่งผู้ใช้บริการอาจไม่สามารถหาได้จากห้องสมุดทั่วไป และไม่มีจําหน่ายในท้องตลาด ปัจจุบันหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่ได้ดําเนินการรวบรวม จัดเก็บ บริการและอนุรักษ์หนังสือหายากที่มีอายุมากกว่า ๑๐๐ ปี ซึ่งทรงคุณค่าไว้เป็นมรดกภูมิปัญญาของชาติ จํานวนทั้งหมด ๔๕๗ ชื่อเรื่อง   ประกอบด้วย ภาษาไทย จํานวน ๓๒๐ ชื่อเรื่องภาษาต่างประเทศ ๑๐๘ เล่ม และภาษาถิ่น (ล้านนา)     จํานวน ๒๙   ชื่อเรื่อง  ซึ่งจากหลักเกณฑ์ในการคัดเลือกพิจารณาลักษณะและคุณค่าของหนังสือหายากตามแนวทางของหอสมุดแห่งชาติรวมทั้ง  พิจารณาเทียบเคียงกับประเภทหนังสือหายากของหอสมุดแห่งชาติ ปรากฏว่าหนังสือหายากของหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่ สามารถจําแนกแบ่งออกเป็น ๑๒ ประเภท ดังนี้
                </p>
              </section>

              <div className="grid gap-12 md:grid-cols-1">

                {/* 1. ตราหอพระสมุดวชิรญาณ */}
                <div id="type-1" className="scroll-mt-28 bg-white border border-amber-200 p-6 rounded-xl hover:shadow-md transition-shadow">
                  <h3 className="text-xl font-bold text-orange-700 mb-3">๑. หนังสือที่มีตราหอพระสมุดวชิรญาณ</h3>
                  <p className="text-gray-700 indent-10 mb-4">เป็นหนังสือที่แสดงถึงความเป็นเจ้าของมาแต่เริ่มแรกและตกทอดมาจนถึงปัจจุบันของหอสมุดแห่งชาติ โดยตราหอพระสมุดวชิรญาณได้มีการเปลี่ยนแปลงจากอดีต ในปี พ.ศ. ๒๔๓๕ จนถึงปัจุบันมีลักษณะของดวงตรา ๗ แบบด้วยกัน ซึ่งตราหอพระสมุดวชิรญาณที่ปรากฏในหนังสือหายากของหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่ พบมีอยู่ ๓ แบบ ได้แก่</p>

                  <div className="mb-6">
                    <p><strong>๑.๑ ตราหอพระสมุดวชิรญาณ</strong> ตรานี้ใช้มาตั้งแต่ พ.ศ. ๒๔๓๘ เป็นรูปพระมหามงกุฏ
                      มีพานสองชั้นรองสมุดและแว่นฟ้าอยู่ด้านซ้ายขวา ด้านล่างเป็นอักษรอริยกะ ความว่า “วชิรญาณ” อาทิ
                      “ประชุมหนังสือเก่า ภาค ๒” หม่อมเจ้า ปิยภักดีนารถ พิมพ์แจกในงานปลงศพสนองคุณหม่อมบาง
                      หม่อมมารดา ปีมโรงอัฐศก พ.ศ. ๒๔๕๗ พิมพ์ที่โรงพิมพ์ไทย ณสพานยศเส</p>
                    <figure className="flex flex-col items-center my-4">
                      <img
                        src="/assets/ภาพที่5.jpg"
                        alt="หนังสือที่มีตราหอพระสมุดวชิรญาณ “ประชุมหนังสือเก่า ภาค ๒”"
                        loading="lazy"
                        className="rounded-lg shadow-sm border border-amber-100 max-w-full h-auto max-h-[300px]"
                      />
                      <figcaption className="text-sm text-gray-600 mt-2 italic">
                        ภาพที่ ๕ หนังสือที่มี
                        ตราหอพระสมุดวชิรญาณ”
                        “ประชุมหนังสือเก่า ภาค ๒”
                        (๒๔๕๗)
                      </figcaption>
                    </figure>
                  </div>

                  <div className="mb-6">
                    <p><strong>๑.๒ ตราหอพระสมุดวชิรญาณสำหรับพระนคร</strong> เป็นรูปวงกลมล้อมรอบพระมหามงกุฏ
                      ที่อยู่ตรงกลาง โดยมีพานสองชั้นรองสมุดและแว่นฟ้าอยู่ด้านซ้ายขวา ด้านบนซ้ายมีเครื่องหมายฟองมัน และ
                      อักษรความว่า “หอพระสมุด” ด้านบนขวามีอักษรความว่า “วชิรญาณ” และเครื่องหมายอังคั่นวิสรรชนีย์
                      ด้านล่างตรงกลางมีอักษรอริยกะความว่า “วชิรญาณ” ด้านบนตรงกลางเหนือพระมหามงกุฏ มีรัศมีส่อง
                      ลงมาทั้งสองข้าง ตราแบบนี้ใช้มาจนถึง พ.ศ. ๒๔๖๘ อาทิ “ประชุมประกาศรัชกาลที่ ๔ ภาค ๔”
                      ประกาศปีมะเมีย พ.ศ. ๒๔๐๑ พิมพ์ในงานพระศพสมเด็จพระเจ้าบรมวงศ์เธอ กรมพระยาเทวะวงศวโรปการ
                      ครบศตมาห ณ วันที่ ๕ ตุลาคม พ.ศ. ๒๔๖๖ พิมพ์ที่โรงพิมพ์โสภณพิพรรฒธนากร
                      พิมพ์ที่โรงพิมพ์หลวง ปี พ.ศ. ๒๔๔๗</p>
                    <figure className="flex flex-col items-center my-4">
                      <img
                        src="/assets/ภาพที่6.jpg"
                        alt="หนังสือที่มีตราหอพระสมุดวชิรญาณสำหรับพระนคร “ประชุมประกาศรัชกาลที่ ๔ ภาค ๔”"
                        loading="lazy"
                        className="rounded-lg shadow-sm border border-amber-100 max-w-full h-auto max-h-[300px]"
                      />
                      <figcaption className="text-sm text-gray-600 mt-2 italic">
                        ภาพที่ ๖ หนังสือที่มีตราหอพระสมุด
                        วชิรญาณสำหรับพระนคร
                        “ประชุมประกาศรัชกาลที่ ๔ ภาค ๔”
                        (๒๔๖๖)
                      </figcaption>
                    </figure>
                  </div>

                  <div className="mb-6">
                    <p><strong>๑.๓ ตราพระราชลัญจกรมังกรคาบแก้ว</strong> เป็นตราที่พระบาทสมเด็จพระปกเกล้าเจ้าอยู่หัว
                      โปรดเกล้าฯ พระราชทานให้ใช้เป็นตราประจำตำแหน่งสภานายกราชบัณฑิตยสถาน เมื่อ พ.ศ. ๒๔๖๙ ครั้น
                      หอพระสมุดวชิรญาณสำหรับพระนครรวมกับราชบัณฑิตยสภาแล้ว จึงใช้ตรานี้มาจนถึงเมื่อมีการประกาศ
                      ยกเลิกราชบัณฑิตยสภาในปี พ.ศ. ๒๔๗๕ อาทิ “จดหมายเหตุเสด็จประพาศต่างประเทศในรัชกาลที่ ๕
                      เสด็จเมืองสิงคโปร์ แลเมืองเบตาเวียครั้งแรก แลเสด็จประพาศประเทศอินเดีย” พิมพ์แจกในงานศพ
                      พระยาอภิรักษ์ราชอุทยาน (แฉล้ม อมาตยกุล) ปีมเสงนพศก พ.ศ. ๒๔๖๐</p>
                    <figure className="flex flex-col items-center my-4">
                      <img
                        src="/assets/ภาพที่7.jpg"
                        alt="หนังสือที่มีตราพระราชลัญจกรมังกรคาบแก้ว “จดหมายเหตุเสด็จประพาสต่างประเทศในรัชกาลที่ ๕”"
                        loading="lazy"
                        className="rounded-lg shadow-sm border border-amber-100 max-w-full h-auto max-h-[300px]"
                      />
                      <figcaption className="text-sm text-gray-600 mt-2 italic">
                        ภาพที่ ๗ หนังสือที่มีตรา
                        พระราชลัญจกรมังกรคาบแก้ว
                        “จดหมายเหตุเสด็จประพาส
                        ต่างประเทศในรัชกาลที่ ๕” (๒๔๖๐)
                      </figcaption>
                    </figure>
                  </div>
                </div>

                {/* 2. หนังสือที่มีประวัติการครอบครอง */}
                <div id="type-2" className="scroll-mt-28 bg-white border border-amber-200 p-6 rounded-xl hover:shadow-md transition-shadow">
                  <h3 className="text-xl font-bold text-orange-700 mb-3">๒. หนังสือที่มีประวัติการครอบครอง</h3>
                  <p className="text-gray-700 indent-10">
                    เป็นหนังสือที่มีหลักฐานหรือประวัติว่าเคยเป็นของผู้ใด
                    มาก่อน และมอบหนังสือที่เคยเป็นเจ้าของ ทั้งจากหน่วยงานและบุคคลให้แก่ทางหอสมุดแห่งชาติรัชมังคลาภิเษก
                    เชียงใหม่ อาทิ “หนังสือประถม ก กา แจก ลูก” บอกอักษรสูง. ต่ำ. แล้ว ให้ผันตามสำเนียงไม้เอก โท ตรี
                    จัตวา สำหรับเด็กหรือผู้ใหญ่ก็เรียนได้ ราคาเล่มละสลึง โรงพิมพ์สามัคคี ตำบลถนนมหาไชย กรุงเทพฯ
                    รัตนโกสินทร์ศก ๑๒๘ (พ.ศ. ๒๔๕๒) ซึ่งหน้าปกในปรากฏตราประทับและใบลงทะเบียนหนังสือของ
                    ห้องสมุดโรงเรียนประจำมณฑลพายัพ “ยุพราชวิทยาลัย” และปรากฏข้อความว่าเป็นหนังสือของ
                    นายทองดี สุจนิล “ให้บำรุงห้องสมุดโรงเรียนประจำมณฑลพายัพ “ยุพราชวิทยาลัย” ณ วันที่ ๗ สิงหาคม
                    “เสภาเรื่องพระราชพงษาวดาร กรุงเก่า” นายหมู่ใหญ่ พระยาโบราณบุรานุรักษ์ ผู้รั้งผู้บังคับกองเสือป่า
                    มณฑลกรุงเก่า พิมพ์เป็นของยินดีแจกสมาชิกกองเสือป่า เพื่อเป็นที่ระลึกถึงวันเสด็จพระราชดำเนินมาทรง
                    เปิดสโมสรเสือป่ามณฑลกรุงเก่า ณ พระราชวังจันทรเกษม ร.ศ. ๑๓๐ (พ.ศ. ๒๔๕๕) ซึ่งภายในตัวเล่ม
                    หนังสือปรากฎข้อความว่า “ให้ไว้แก่ห้องสมุดเชียงใหม่ พืชน์ เดชะคุปต์ ๑๓/๕/๗๐”
                  </p>

                  <div className="flex flex-wrap justify-center gap-4 mt-8 items-stretch">
                    <ImageCard
                      src="/assets/ภาพที่8.jpg"
                      alt="ใบลงทะเบียนหนังสือของห้องสมุดโรงเรียนประจำมณฑลพายัพฯ"
                      captionTitle="ภาพที่ ๘ ใบลงทะเบียนหนังสือของห้องสมุดโรงเรียนประจำมณฑลพายัพฯ"
                      captionDetail="“หนังสือประถม ก กา แจกลูก” (๒๔๕๒)"
                    />
                    <ImageCard
                      src="/assets/ภาพที่9.jpg"
                      alt="ตราประจำห้องสมุดและลายมือชื่อเจ้าของหนังสือที่มอบให้แก่ห้องสมุด"
                      captionTitle="ภาพที่ ๙ ตราประจำห้องสมุดและลายมือชื่อเจ้าของหนังสือที่มอบให้แก่ห้องสมุด"
                      captionDetail="“หนังสือประถม ก กา แจกลูก” (๒๔๕๒)"
                    />
                    <ImageCard
                      src="/assets/ภาพที่10.jpg"
                      alt="ข้อความให้ไว้แก่ห้องสมุดเชียงใหม่ลายมือชื่อเจ้าของผู้มอบและวันที่มอบฯ"
                      captionTitle="ภาพที่ ๑๐ ข้อความให้ไว้แก่ห้องสมุดเชียงใหม่ลายมือชื่อเจ้าของผู้มอบและวันที่มอบฯ"
                      captionDetail="“เสภาเรื่องพระราชพงษาวดาร กรุงเก่า” ๒๔๕๕"
                    />
                  </div>
                </div>

                {/* 3. หนังสือที่มีลักษณะโดดเด่น */}
                <div id="type-3" className="scroll-mt-28 bg-white border border-amber-200 p-6 rounded-xl hover:shadow-md transition-shadow">
                  <h3 className="text-xl font-bold text-orange-700 mb-3">๓. หนังสือที่มีลักษณะโดดเด่น เนื้อหาน่าสนใจ</h3>
                  <figure className="flex flex-col items-center w-full max-w-[350px] mx-auto my-8">
                    <div className="flex flex-row justify-center items-stretch w-full gap-0.5 bg-gray-50 border border-amber-100 rounded-lg overflow-hidden">
                      <div className="w-1/2 relative">
                        <img src="/assets/ภาพที่11.jpg" alt="ปกหนังสือ นิทรา ชาคริช" loading="lazy" className="w-full h-full object-cover" />
                      </div>
                      <div className="w-1/2 relative">
                        <img src="/assets/ภาที่11.jpg" alt="เนื้อหาภายในหนังสือ นิทรา ชาคริช" loading="lazy" className="w-full h-full object-cover" />
                      </div>
                    </div>
                    <figcaption className="mt-4 text-center text-gray-700 text-sm">
                      <strong className="block mb-1 text-orange-800 text-base">
                        ภาพที่ ๑๑ หนังสือที่มีลักษณะเด่น เนื้อหาน่าสนใจ
                      </strong>
                      <span>“นิทรา ชาคริช” (๒๔๕๕)</span>
                    </figcaption>
                  </figure>
                  <p className="text-gray-700 indent-10">เป็นหนังสือที่มีลักษณะโดดเด่น น่าสนใจ รวมถึงหนังสือที่เขียน
                    โดยปราศจากความจริง วรรณกรรมแปลง อาทิ “นิทรา ชาคริช”
                    พระราชนิพนธ์ในพระบาทสมเด็จพระจุลจอมเกล้าเจ้าอยู่หัว
                    (รัชกาลที่ ๕) พิมพ์ครั้งที่สาม ๕,๐๐๐ ฉบับ โดยอนุญาตจาก
                    กระทรวงธรรมการ ศก ๒๔๕๕ ปกแข็งหุ้มผ้า ราคาเล่มละ
                    ๘๕ สตางค์ โรงพิมพ์อักษรนิติ์ เป็นหนังสือที่ได้เก็บเนื้อความ
                    ในนิทานเรื่องหนึ่งของอาเรเบียนไนต์ เอนเตอเตนเมนต์(Arabian night entertainment) ซึ่งเรียกชื่อว่า สลีเปอ อะเวเกน (Sleeper Awaken) เป็นเรื่องราว
                    รวบรวมนิทานต่าง ๆ ในประเทศอินเดีย อาหรับ แอฟริกา ซึ่งเป็นหนังสือที่มีการพิมพ์เป็นหลายภาษา
                    ทั้งภาษาฝรั่งเศสและภาษาอังกฤษ</p>
                </div>

                {/* 4. โรงพิมพ์ยุคแรก */}
                <div id="type-4" className="scroll-mt-28 bg-white border border-amber-200 p-6 rounded-xl hover:shadow-md transition-shadow">
                  <h3 className="text-xl font-bold text-orange-700 mb-3">๔. หนังสือที่พิมพ์โดยโรงพิมพ์ยุคแรก ๆ ของประเทศไทย</h3>
                  <p className="text-sm text-gray-700">เป็นหนังสือที่ได้มีการพิมพ์โดยโรงพิมพ์
                    ในยุคแรก ๆ ของประเทศไทย รวมทั้งโรงพิมพ์ในยุคแรก ๆ ของท้องถิ่น อาทิ “MATTHEW เรื่องประเสริฐ
                    ของพระเยซูคริศต์” เรียบเรียงโดยท่านมัดธาย แปลออกจากภาษาเฮเลน พิมพ์ครั้งที่ ๗ จำนวนพิมพ์
                    ๔,๐๐๐ เล่ม ตีพิมพ์ที่โรงพิมพ์ครูอเมริกัน คริศต์ศักราช ๑๙๑๐ (พ.ศ. ๒๔๕๓) “เรื่อง เต็ลมา ภาคที่สอง
                    เล่ม ๑” โดย ศ.ร. พิมพ์ที่โรงพิมพ์กรุงเทพฯ เดลิเมล์ ถนนสี่พระยา กรุงเทพฯ พระพุทธศักราช ๒๔๕๕ ราคา
                    เล่มละ ๕๐ สตางค์ และ“คำแนะนำ พระอธิการวัด, เจ้าคณะหมวด, เจ้าคณะแขวง และพระอุปัชฌาย์
                    ในจังหวัดลำพูน ของพระครูญาณมงคล เจ้าคณะจังหวัดลำพูน” พ.ศ. ๒๔๖๒ พิมพ์ครั้งที่ ๑ จำนวน
                    ๕๐๐ เล่ม พิมพ์ที่โรงพิมพ์อุปโยคินบำรุงประเทศเจริญ บ้านฮ่อ จังหวัดเชียงใหม่</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-8 items-start">
                    <figure className="flex flex-col h-full">
                      <div className="flex flex-row justify-center items-stretch gap-2 bg-gray-50 border border-amber-100 p-2 rounded-lg">
                        <div className="w-1/2">
                          <img src="/assets/ภาพที่12.jpg" alt="MATTHEW ปกหน้า" loading="lazy" className="w-full h-64 object-contain rounded-sm" />
                        </div>
                        <div className="w-1/2">
                          <img src="/assets/ภาที่12.jpg" alt="MATTHEW ปกใน" loading="lazy" className="w-full h-64 object-contain rounded-sm" />
                        </div>
                      </div>
                      <figcaption className="mt-4 text-center text-gray-700 text-sm">
                        <strong className="block mb-1 text-orange-800">
                          ภาพที่ ๑๒ หนังสือที่พิมพ์โดยโรงพิมพ์ยุคแรก ๆ ของไทย
                        </strong>
                        <span>“MATTHEW เรื่องประเสริฐของพระเยซูคริศต์” (๒๔๕๓)</span>
                      </figcaption>
                    </figure>

                    <figure className="flex flex-col h-full">
                      <div className="flex justify-center items-center bg-gray-50 border border-amber-100 p-2 rounded-lg h-full">
                        <img src="/assets/ภาพที่13.jpg" alt="คำแนะนำพระอธิการวัด" loading="lazy" className="h-64 w-auto object-contain rounded-sm shadow-sm" />
                      </div>
                      <figcaption className="mt-4 text-center text-gray-700 text-sm">
                        <strong className="block mb-1 text-orange-800">
                          ภาพที่ ๑๓ หนังสือที่พิมพ์โดยโรงพิมพ์ยุคแรก ๆ ของเชียงใหม่
                        </strong>
                        <span>“คำแนะนำ พระอธิการวัด, เจ้าคณะหมวด, เจ้าคณะแขวง และพระอุปัชฌาย์ในจังหวัดลำพูนฯ (๒๔๖๒)</span>
                      </figcaption>
                    </figure>
                  </div>
                </div>

                {/* 5. พิมพ์ครั้งแรก */}
                <div id="type-5" className="scroll-mt-28 bg-white border border-amber-200 p-6 rounded-xl hover:shadow-md transition-shadow">
                  <h3 className="text-xl font-bold text-orange-700 mb-3">๕. หนังสือที่จัดพิมพ์เป็นครั้งแรก</h3>
                  <p className="text-gray-700 indent-10">
                    คือ หนังสือที่มีการจัดพิมพ์ต้นฉบับออกมาในรูปเล่มของหนังสือ
                    เป็นครั้งแรก เป็นฉบับที่เสนอข้อเท็จจริง ความคิดเห็น ประสบการณ์ หรือเจตนาที่แท้จริงที่สุดของผู้เขียน
                    ซึ่งเดิมหนังสือเหล่านี้มักถ่ายทอดภาษามาจากหนังสือสมุดไทย หรือองค์ความรู้ตามยุคสมัย อาทิ “หนังสือ
                    ธัม์มจัก์กัป์ปวัต์ตนสูตร์” พระมหาแจ่มเปรียญเอกวัดมกุฎกษัตริยาราม แปล อนัต์ตลักขณสุตร์ อาทิต์ตป
                    ริยายสุตร์ พระมหาศุขเปรียญ ๕ ประโยค วัดราชบพิธ แปล หลวงดำรงธรรมสารสร้างไว้ในพะพุทธสาสนา
                    พิมพ์ครั้งแรก ๕,๐๐๐ ฉบับ รัตนโกสิน ศก ๑๒๔ (พ.ศ. ๒๔๔๘) โรงพิมพ์บำรุงนุกูลกิจ และ “เรื่องพระปฐมเจดีย์” เจ้าพระยาทิพากรวงษ์ เรียบเรียง ฉบับหอพระสมุดวชิรญาณ พิมพ์ครั้งแรก สำหรับจำหน่ายที่พระปฐมเจดีย์
                    โรงพิมพ์ไทย สพานยศเส กรุงเทพฯ ร.ศ. ๑๒๘๒๖ (พ.ศ. ๒๔๕๒)
                  </p>
                  <div className="flex flex-wrap justify-center gap-4 mt-8 items-stretch">
                    <ImageCard
                      src="/assets/ภาพที่14.jpg"
                      alt="หนังสือธัม์มจัก์กัป์ปวัต์ตนสูตร์ พิมพ์ครั้งแรก"
                      captionTitle="ภาพที่ ๑๔ หนังสือที่จัดพิมพ์เป็นครั้งแรก"
                      captionDetail="“หนังสือธัม์มจัก์กัป์ปวัต์ตนสูตร์” (๒๔๔๘)"
                    />
                    <ImageCard
                      src="/assets/ภาพที่15.jpg"
                      alt="เรื่องพระปฐมเจดีย์ พิมพ์ครั้งแรก"
                      captionTitle="ภาพที่ ๑๕ หนังสือที่จัดพิมพ์เป็นครั้งแรก"
                      captionDetail="“เรื่องพระปฐมเจดีย์” (๒๔๕๒)"
                    />
                  </div>
                </div>

                {/* 6. บุ๊คเพลท */}
                <div id="type-6" className="scroll-mt-28 bg-white border border-amber-200 p-6 rounded-xl hover:shadow-md transition-shadow">
                  <h3 className="text-xl font-bold text-orange-700 mb-3">๖. หนังสือที่มีบุ๊คเพลทหรือบรรณสิทธิ์</h3>
                  <p className="text-gray-700 indent-10">
                    เป็นหนังสือที่มีป้ายหรือตราประจำตัวของหนังสือ และ
                    หนังสือที่เจ้าของเดิมมอบให้ผู้อื่น แสดงความเป็นเจ้าของหนังสือ ที่มีรูปแบบลวดลายต่างกัน ติดไว้ด้านใน
                    ของปกหน้าหรือที่ใบรองปก อาทิ “แผนที่เดินทางในบริเวณมณฑลราชบุรี มาตราส่วน ๑:๒๐๐,๐๐๐”๒๗
                    ภายในเล่มปรากฏบุ๊คเพลทหรือบรรณสิทธิ์ของพระบาทสมเด็จพระมงกุฎเกล้าเจ้าอยู่หัวปรากฏอยู่ด้านใน
                    ของปกหน้า ลักษณะโดดเด่นที่น่าสนใจของหนังสือ เมื่อพับเก็บจะมีขนาดเล็กกว่าขนาดหนังสือปกติทั่วไป
                    ตัวเล่มจะคล้ายกับคู่มือขนาดกะทัดรัดพกพาได้ง่าย แต่เมื่อเปิดออกจะปรากฏภาพแผนที่ขนาดใหญ่ ๑ แผ่น
                    บอกรายละเอียดว่าเป็น “แผนที่เดินทางในบริเวณบ้านโป่ง-เพ็ชร์บุรี-และปลายลำแม่น้ำภาชี มาตราส่วน ๑:
                    ๒๐๐,๐๐๐” ตัววัสดุแผนที่เป็นกระดาษผ้าทั้งแผ่น และหน้าปกประดับชื่อหนังสือและกรอบล้อมรอบสีทอง
                    ซึ่งแตกต่างจากหนังสือส่วนใหญ่ที่จะนิยมพิมพ์ตัวอักษรสีดำ
                  </p>
                  <figure className="flex flex-col items-center w-full max-w-[600px] mx-auto my-8">
                    <div className="flex flex-row justify-center items-stretch w-full gap-0.5 bg-gray-50 border border-amber-100 rounded-lg overflow-hidden">
                      <div className="w-1/3 relative">
                        <img src="/assets/ภาพที่16.jpg" alt="ปกหนังสือแผนที่เดินทางฯ" loading="lazy" className="w-full h-full object-cover" />
                      </div>
                      <div className="w-1/3 relative">
                        <img src="/assets/ภาที่16.jpg" alt="บุ๊คเพลทพระบาทสมเด็จพระมงกุฎเกล้าเจ้าอยู่หัว" loading="lazy" className="w-full h-full object-cover" />
                      </div>
                      <div className="w-1/2 relative">
                        <img src="/assets/ภที่16.jpg" alt="แผนที่เดินทางในบริเวณปริมณฑลราชบุรี" loading="lazy" className="w-full h-full object-cover" />
                      </div>
                    </div>
                    <figcaption className="mt-4 text-center text-gray-700 text-sm">
                      <strong className="block mb-1 text-orange-800 text-base">
                        ภาพที่ ๑๖ หนังสือที่มีบุ๊คเพลทฯ ประจำพระองค์ของพระบาทสมเด็จพระมงกุฎเกล้าเจ้าอยู่หัว
                      </strong>
                      <span>“แผนที่เดินทางในบริเวณปริมณฑลราชบุรี มาตราส่วน ๑:๒๐๐,๐๐๐” (๒๔๕๘)</span>
                    </figcaption>
                  </figure>
                </div>

                {/* 7. โอกาสพิเศษ */}
                <div id="type-7" className="scroll-mt-28 bg-white border border-amber-200 p-6 rounded-xl hover:shadow-md transition-shadow">
                  <h3 className="text-xl font-bold text-orange-700 mb-6">
                    ๗. หนังสือที่ออกในโอกาสพิเศษ
                  </h3>
                  <div className="flex flex-col md:flex-row items-start gap-6 mb-8">
                    <div className="w-full md:w-3/4">
                      <p className="text-gray-700 indent-10 leading-relaxed text-justify">
                        เป็นหนังสือที่จัดพิมพ์ในโอกาสต่าง ๆ เพื่อเป็นที่ระลึกเนื่องในโอกาสต่าง ๆ
                        ทั้งที่ระลึกในวันเกิด งานพระราชทานเพลิงศพ งานพระราชทานกฐิน เป็นต้น อาทิ{" "}
                        <strong>“สาราทานปริยาย กถามรค”</strong> โดยพระพุทธภาสิตสูตร
                        มาในพระคัมภีรปัญจกนิบาต อังคุตตรนิกาย วัดราชประดิษฐสถิตมหาสีมาราม พิมพ์
                        ๒,๐๐๐ ฉบับ เดือนสี่ ปีมะโรงโทศก จุลศักราช ๑๒๔๒ (พ.ศ.๒๔๒๓)
                        ปัจจุบันมีอายุ ๑๔๓ ปี ถือว่าเป็น{" "}
                        <strong>
                          “หนังสือหายากภาษาไทยที่มีอายุเก่าที่สุดในหอสมุดแห่งชาติรัชมังคลาภิเษก
                          เชียงใหม่”
                        </strong>{" "}
                        จากหลักฐานกล่าวถึงหนังสือ “สาราทานปริยาย กถามรค”
                        เป็นหนังสือที่พระบาทสมเด็จพระจุลจอมเกล้าเจ้าอยู่หัวทรงโปรดเกล้าฯ
                        ให้พิมพ์แจกในงานพระเมรุพระศพสมเด็จพระนางเจ้าสุนันทากุมารีรัตน์พระบรมราชเทวี
                        (สมเด็จพระนางเรือล่ม) และสมเด็จพระเจ้าลูกเธอ เจ้าฟ้ากรรณาภรณ์ เพชรรัตน์
                        ซึ่งทรงสิ้นพระชนม์พร้อมกับพระราชมารดา หนังสือเล่มนี้ยังถือว่าเป็น
                        “หนังสือสวดมนต์ภาษาไทยเล่มแรกที่ตีพิมพ์ในประเทศไทย”
                        เนื้อหาเป็นบทสวดมนต์คำนมัสการและบูชาพระ รวมพระสูตรและพระปริตต่าง ๆ
                      </p>
                    </div>
                    <div className="w-full md:w-1/4 flex flex-col items-center">
                      <img src="/assets/ภาพที่17.jpg" alt="สาราทานปริยาย กถามรค หนังสือหายาก" loading="lazy" className="w-full h-auto object-contain rounded shadow-sm border border-amber-100" />
                      <figcaption className="mt-2 text-center text-gray-700 text-sm">
                        <strong className="block text-orange-800">
                          ภาพที่ ๑๗ หนังสือหายากภาษาไทยที่เก่าที่สุดของหอสมุดแห่งชาติฯ เชียงใหม่
                        </strong>
                        <span>“สาราทานปริยาย กถามรค” (พ.ศ. ๒๔๒๓)</span>
                      </figcaption>
                    </div>
                  </div>
                  <div className="flex flex-col md:flex-row items-start gap-6">
                    <div className="w-full md:w-1/4 flex flex-col items-center">
                      <img src="/assets/ภาพที่18.jpg" alt="ราชสกุลวงศ์ หนังสือหายาก" loading="lazy" className="w-full h-auto object-contain rounded shadow-sm border border-amber-100" />
                      <figcaption className="mt-2 text-center text-gray-700 text-sm">
                        <strong className="block text-orange-800">
                          ภาพที่ ๑๘ หนังสือรวบรวมราชสกุลวงศ์ฯ ในกรุงรัตนโกสินทร์
                        </strong>
                        <span>“ราชสกุลวงศ์ฯ” (พ.ศ. ๒๔๖๓)</span>
                      </figcaption>
                    </div>
                    <div className="w-full md:w-3/4">
                      <p className="text-gray-700 leading-relaxed text-justify">
                        <strong>
                          “ราชสกุลวงศ์ พระนามเจ้าฟ้าแลพระองค์เจ้าในกรุงรัตนโกสินทร์”
                        </strong>{" "}
                        สมเด็จพระเจ้าน้องยาเธอ เจ้าฟ้ากรมขุนศุโขไทยธรรมราชา
                        ทรงพิมพ์เป็นทักษิณานุประทาน
                        อุทิศส่วนพระกุศลสนองพระเดชพระคุณสมเด็จพระศรีพัชรินทราบรมราชินีนาถ
                        พระบรมนาพระบรมราชชนนีพระพันปีหลวง ในงานถวายพระเพลิงพระบรมศพ
                        ที่พระเมรุมาศท้องสนามหลวง เมื่อวันที่ ๒๔ พฤษภาคม พ.ศ. ๒๔๖๓
                        พิมพ์ที่โรงพิมพ์ไท กรุงเทพฯ กล่าวเริ่มถึงลำดับปฐมวงศ์
                        พระนามพระราชโอรสธิดาในรัชกาลที่ ๑ - รัชกาลที่ ๕
                        นับว่าเป็นหนังสือหายากที่มีคุณค่าทางด้านประวัติศาสตร์
                        ซึ่งได้รวบรวมราชสกุลวงศ์ในกรุงรัตนโกสินทร์ที่สมบูรณ์อีกเล่ม
                      </p>
                    </div>
                  </div>
                </div>

                {/* 8. รูปเล่มสวยงาม */}
                <div id="type-8" className="scroll-mt-28 bg-white border border-amber-200 p-6 rounded-xl hover:shadow-md transition-shadow">
                  <h3 className="text-xl font-bold text-orange-700 mb-4">
                    ๘. หนังสือที่มีรูปเล่มสวยงาม
                  </h3>
                  <p className="text-gray-700 indent-10 leading-relaxed text-justify mb-8">
                    เป็นหนังสือที่มีปกสวยงามเป็นพิเศษ ทำปกด้วยหนังแท้มีลวดลายเป็นทอง
                    หนังสือที่มีภาพประกอบเป็นฝีมือของจิตรกรเอก หรือบุคคลสำคัญ
                    หนังสือที่พิมพ์ด้วยกระดาษอย่างดี ราคาแพง อาทิ <strong>“นิราศมะเหลเถไถ”</strong>{" "}
                    แถลงเรื่องเสด็จพระราชดำเนินทางชลมารคในมณฑลอยุธยาและนครสวรรค์ พ.ศ. ๒๔๖๕
                    โดยศรีอยุธยา <strong>“รามเกียรติ์ บทร้องและบทพากย์”</strong>{" "}
                    พระราชนิพนธ์ในพระบาทสมเด็จพระรามาธิบดีศรีสินทร มหาวชิราวุธ
                    พระมงกุกเกล้าเจ้าอยู่หัว
                    พระราชทานพระบรมราชานุญาตให้พิมพ์แจกในงานฉลองสุพรรณบัฏ
                    (แผ่นทองที่เขียนจารึกพระนามเจ้าที่ตั้งเป็นกรมนั้น) จางวางเอก แลนายพลโท
                    เจ้าพระยารามราฆพ ณ วันที่ ๑๐ ธันวาคม พ.ศ. ๒๔๖๔{" "}
                    <strong>“บ่วงมาร ลครพูด ๔ ชุด”</strong>{" "}
                    ของศรีอยุธยา (พระบาทสมเด็จพระมงกุฎเกล้าเจ้าอยู่หัว) แต่งเสร็จเมื่อวันที่ ๒
                    พฤศจิกายน พ.ศ. ๒๔๕๗ นับว่าเป็นหนังสือหายากที่มีรูปเล่มสวยงาม
                    ด้วยลักษณะของหน้าปกทำด้วยผ้าสีต่าง ๆ ลวดลายและตัวหนังสือสีทองสวยงาม
                    พร้อมทั้งปรากฏตราสัญลักษณ์พระปรมาภิไธยย่อ
                    ซึ่งแตกต่างจากหน้าปกของหนังสือหายากทั่วไป
                    จากข้อสังเกตลักษณะหน้าปกแสดงถึงอัตลักษณ์หนังสือพระราชนิพนธ์ของพระองค์ได้อย่างชัดเจน
                  </p>
                  <div className="flex flex-wrap justify-center gap-6 items-start">
                    <figure className="flex flex-col items-center w-full md:w-[30%]">
                      <div className="w-full h-80 bg-white border border-amber-100 rounded-lg flex items-center justify-center p-2 mb-3 shadow-sm">
                        <img src="/assets/ภาพที่19.jpg" alt="นิราศมะเหลเถไถ" loading="lazy" className="max-h-full max-w-full object-contain" />
                      </div>
                      <figcaption className="text-center text-gray-700 text-sm">
                        <strong className="block text-orange-800 mb-1">
                          ภาพที่ ๑๙ หนังสือที่มีรูปเล่มสวยงาม
                        </strong>
                        <span>“นิราศมะเหลเถไถ” (พ.ศ. ๒๔๖๕)</span>
                      </figcaption>
                    </figure>
                    <figure className="flex flex-col items-center w-full md:w-[30%]">
                      <div className="w-full h-80 bg-white border border-amber-100 rounded-lg flex items-center justify-center p-2 mb-3 shadow-sm">
                        <img src="/assets/ภาพที่20.jpg" alt="รามเกียรติ์" loading="lazy" className="max-h-full max-w-full object-contain" />
                      </div>
                      <figcaption className="text-center text-gray-700 text-sm">
                        <strong className="block text-orange-800 mb-1">
                          ภาพที่ ๒๐ หนังสือที่มีปกสวยงาม
                        </strong>
                        <span>“รามเกียรติ์ บทร้องและบทพากย์” (พ.ศ. ๒๔๖๔)</span>
                      </figcaption>
                    </figure>
                    <figure className="flex flex-col items-center w-full md:w-[30%]">
                      <div className="w-full h-80 bg-white border border-amber-100 rounded-lg flex items-center justify-center p-2 mb-3 shadow-sm">
                        <img src="/assets/ภาพที่21.jpg" alt="บ่วงมาร" loading="lazy" className="max-h-full max-w-full object-contain" />
                      </div>
                      <figcaption className="text-center text-gray-700 text-sm">
                        <strong className="block text-orange-800 mb-1">
                          ภาพที่ ๒๑ หนังสือที่มีรูปเล่มสวยงาม
                        </strong>
                        <span>“บ่วงมาร ลครพูด ๔ ชุด” (พ.ศ. ๒๔๕๗)</span>
                      </figcaption>
                    </figure>
                  </div>
                </div>

                {/* 9. ภาพเก่า */}
                <div id="type-9" className="scroll-mt-28 bg-white border border-amber-200 p-6 rounded-xl hover:shadow-md transition-shadow">
                  <h3 className="text-xl font-bold text-orange-700 mb-4">
                    ๙. หนังสือที่มีภาพเก่าที่น่าสนใจ
                  </h3>
                  <div className="text-gray-700 indent-10 leading-relaxed text-justify mb-8 space-y-4">
                    <p>
                      คือ หนังสือที่มีภาพเก่าสวยงาม มีคุณค่า อาทิ{" "}
                      <strong>“พระราชนิพนธ์เรื่องไกลบ้าน”</strong> ฉบับมีรูปภาพ เล่ม ๑
                      และจดหมายเหตุประกอบเรื่องไกลบ้านว่าด้วยการส่งเสด็จ
                      การรักษาพระนครเวลาเสด็จไม่อยู่ และการรับเสด็จกลับคืนพระนคร
                      คราวพระบาทสมเด็จฯ พระจุลจอมเกล้าเจ้าอยู่หัว เสด็จไปยุโรปครั้งหลัง
                      พระเจ้าบรมวงศ์เธอ กรมพระดำรงราชานุภาพ ทรงรวบรวมและจัดพิมพ์ในงานฉลองพระชันษา
                      พระอรรคชายาเธอ พระองค์เจ้าสายสวลีภิรมย์ กรมขุนสุทธาสินีนาฏ เมื่อปีกุน พ.ศ.
                      ๒๔๖๖ โรงพิมพ์โสภณพิพรรฒธนากร
                    </p>
                    <p>
                      <strong>
                        “Journal Du Voyage De Siam Fait En 1685 & 1686”
                        (จดหมายเหตุรายวันการเดินทางไปสู่ประเทศสยาม ในปี ค.ศ. ๑๖๘๕ และ ๑๖๘๖)
                      </strong>{" "}
                      ของ M. L'Abbe de Choisy (บาทหลวง เดอ ชัวซีย์) จัดพิมพ์โดย Editions
                      Duchartre et Van Buggenhoudt เป็นภาษาฝรั่งเศส
                      เป็นบันทึกความทรงจำการเดินทาง เมื่อครั้งที่คณะราชทูตของพระเจ้าหลุยส์ที่ ๑๔
                      พระเจ้ากรุงฝรั่งเศส เชิญพระราชสาสน์มายังสมเด็จพระนารายณ์มหาราช
                      สมเด็จเจ้ากรุงสยาม โดยบาทหลวง เดอ ชัวซีย์ ทำการเขียนบันทึกเป็น
                      จดหมายเหตุประจำวันตลอดการเดินทาง จะขาดอยู่เพียงวันเดียวเท่านั้นที่ไม่มีในต้นฉบับ
                      คือ วันที่ ๕ มิถุนายน พ.ศ. ๒๒๒๙ จดหมายเหตุรายวันฉบับนี้นับว่าเป็นเอกสารสำคัญที่แสดงให้เห็นถึง
                      การเดินทาง พลเมือง โรคภัยไข้เจ็บ การทูต การเมืองและการปกครอง
                      ขนบธรรมเนียมวัฒนธรรม ในช่วงยุคสมัยนั้น พร้อมทั้งมีภาพประกอบที่ทรงคุณค่า
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                    <figure className="flex flex-col items-center">
                      <div className="flex gap-2 w-full mb-3 bg-white border border-amber-100 p-2 rounded-lg">
                        <div className="w-1/2 relative">
                          <img src="/assets/ภาพที่22_1.jpg" alt="ภาพสมเด็จพระบรมราชินีนาถฯ กลับจากส่งเสด็จ" loading="lazy" className="w-full h-auto object-contain rounded-sm" />
                        </div>
                        <div className="w-1/2 relative">
                          <img src="/assets/ภาพที่22_2.jpg" alt="รูปหมู่ก่อนเสด็จขึ้นบกที่ปอตเสด" loading="lazy" className="w-full h-auto object-contain rounded-sm" />
                        </div>
                      </div>
                      <figcaption className="text-center text-gray-700 text-sm">
                        <strong className="block text-orange-800 mb-1">
                          ภาพที่ ๒๒ ภาพสมเด็จพระบรมราชินีนาถฯ กลับจากส่งเสด็จในเรือพระที่นั่งมหาจักรี
                          และ รูปหมู่ก่อนเสด็จขึ้นบกที่ปอตเสด
                        </strong>
                        <span>“พระราชนิพนธ์เรื่องไกลบ้าน” (พ.ศ. ๒๔๖๖)</span>
                      </figcaption>
                    </figure>
                    <figure className="flex flex-col items-center">
                      <div className="w-full h-64 mb-3 bg-white border border-amber-100 p-2 rounded-lg flex items-center justify-center">
                        <img src="/assets/ภาพที่23.jpg" alt="Journal Du Voyage De Siam" loading="lazy" className="max-h-full max-w-full object-contain" />
                      </div>
                      <figcaption className="text-center text-gray-700 text-sm">
                        <strong className="block text-orange-800 mb-1">
                          ภาพที่ ๒๓ หนังสือที่มีภาพทรงคุณค่าทางประวัติศาสตร์ไทยซึ่งบันทึกโดยชาวต่างชาติ
                        </strong>
                        <span>“Journal Du Voyage De Siam Fait En 1685 & 1686”</span>
                      </figcaption>
                    </figure>
                  </div>
                </div>

                {/* 10. ชีวประวัติ */}
                <div id="type-10" className="scroll-mt-28 bg-white border border-amber-200 p-6 rounded-xl hover:shadow-md transition-shadow">
                  <h3 className="text-xl font-bold text-orange-700 mb-4">
                    ๑๐. หนังสือชีวประวัติของบุคคล
                  </h3>
                  <p className="text-gray-700 indent-10 leading-relaxed text-justify mb-6">
                    เป็นหนังสือที่มีเนื้อหากล่าวถึงประวัติบุคคล อาทิ{" "}
                    <strong>
                      “ประวัติหลวงสันทิศวิทยาสัณห์ (เกลี้ยง สุนทรรัตน์) กับ ปกิณ์กธรรม”
                    </strong>{" "}
                    กลีบ สุมนนัฏ พิมพ์แจกไว้อาลัยในงานศพหลวงสันทิศวิทยาสัณห์ ผู้เป็นหลาน
                    พระพุทธศักราช ๒๔๖๑
                  </p>
                  <figure className="flex flex-col items-center w-full mt-4">
                    <div className="bg-white border border-amber-100 p-2 rounded-lg shadow-sm max-w-sm">
                      <img src="/assets/ภาพที่24.jpg" alt="ประวัติหลวงสันทิศวิทยาสัณห์" loading="lazy" className="w-full h-auto object-contain rounded-sm" />
                    </div>
                    <figcaption className="mt-3 text-center text-gray-700 text-sm">
                      <strong className="block text-orange-800 mb-1">
                        ภาพที่ ๒๔ หนังสือที่มีชีวประวัติบุคคล
                      </strong>
                      <span>“ประวัติหลวงสันทิศวิทยาสัณห์ฯ” (พ.ศ. ๒๔๖๑)</span>
                    </figcaption>
                  </figure>
                </div>

                {/* 11. ลายพระราชหัตถเลขา */}
                <div id="type-11" className="scroll-mt-28 bg-white border border-amber-200 p-6 rounded-xl hover:shadow-md transition-shadow">
                  <h3 className="text-xl font-bold text-orange-700 mb-4">
                    ๑๑. เอกสารต้นฉบับลายพระราชหัตถเลขา และลายมือเขียน
                  </h3>
                  <p className="text-gray-700 indent-10 leading-relaxed text-justify mb-8">
                    เป็นเอกสารที่เป็นต้นฉบับลายพระราชหัตถเลขาหรือลายมือเขียน อาทิ{" "}
                    <strong>
                      “เรื่องตามใจท่าน ลครเริงรมย์ของวิลเลียม เชกส์เปียร์”
                    </strong>{" "}
                    สมเด็จพระรามาธิบดีศรีสินทร มหาวชิราวุธ พระมงกุฎเกล้าเจ้าแผ่นดินสยาม
                    ทรงแปลจากเรื่อง As You Like It และประพันธ์เป็นกาพย์ภาษาไทย มีคำนำพิเศษซึ่งเป็น
                    Facsimile ลายพระราชหัตถ์ของรัชกาลที่ ๖ ปรากฏอยู่ในเล่ม
                    ซึ่งหนังสือเล่มนี้ได้รวบรวมขึ้นเพื่ออนุรักษ์ผลงานอันทรงคุณค่า
                    ควรแก่การศึกษา ค้นคว้าให้สืบทอดคงอยู่เป็นมรดกทางวรรณกรรมของชาติสืบต่อไป{" "}
                    <strong>
                      “พระราชหัตถเลขาส่วนพระองค์ สมเด็จพระรามาธิบดี ศรีสินทรมหาจุฬาลงกรณ์
                      พระจุลจอมเกล้าเจ้าอยู่หัว”
                    </strong>{" "}
                    ทรงมีพระราชทานแด่ สมเด็จพระศรีพัชรินทรา บรมราชินีนารถ พระบรมราชชนนี
                    ในวันที่ทรงสำเร็จราชการแผ่นดินต่างพระองค์เมื่อเสด็จพระราชดำเนินประพาสยุโรป
                    พ.ศ. ๒๔๔๐ ได้ทรงพระกรุณาโปรดเกล้าฯ
                    ให้พิมพ์พระราชทานเป็นที่รฤกในงานพระบรมศพ เดือนพฤษภาคม พ.ศ. ๒๔๖๓
                    โรงพิมพ์โสภณพิพรรฒธนากร
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                    <figure className="flex flex-col items-center">
                      <div className="flex gap-2 w-full mb-3 bg-white border border-amber-100 p-2 rounded-lg items-stretch">
                        <div className="w-1/2 relative">
                          <img src="/assets/ภาพที่25_1.jpg" alt="ปกเรื่องตามใจท่าน" loading="lazy" className="w-full h-72 object-contain rounded-sm" />
                        </div>
                        <div className="w-1/2 relative">
                          <img src="/assets/ภาพที่25_2.jpg" alt="ลายพระราชหัตถเลขา ร.๖" loading="lazy" className="w-full h-72 object-contain rounded-sm" />
                        </div>
                      </div>
                      <figcaption className="text-center text-gray-700 text-sm">
                        <strong className="block text-orange-800 mb-1">
                          ภาพที่ ๒๕ หนังสือที่ปรากฏพระราชหัตถเลขา (ลายมือเขียน)
                          ของพระบาทสมเด็จพระมงกุฎเกล้าเจ้าอยู่หัว (รัชกาลที่ ๖)
                        </strong>
                        <span>“เรื่องตามใจท่านฯ” (พ.ศ. ๒๔๖๑)</span>
                      </figcaption>
                    </figure>
                    <figure className="flex flex-col items-center">
                      <div className="w-full mb-3 bg-white border border-amber-100 p-2 rounded-lg flex items-center justify-center">
                        <div className="w-full">
                          <img src="/assets/ภาพที่26.jpg" alt="พระราชหัตถเลขา ร.๕" loading="lazy" className="w-full h-72 object-contain rounded-sm" />
                        </div>
                      </div>
                      <figcaption className="text-center text-gray-700 text-sm">
                        <strong className="block text-orange-800 mb-1">
                          ภาพที่ ๒๖ หนังสือพระราชหัตถเลขาฯ
                        </strong>
                        <span>“พระราชหัตถเลขาส่วนพระองค์ฯ” (พ.ศ. ๒๔๖๓)</span>
                      </figcaption>
                    </figure>
                  </div>
                </div>

                {/* 12. ภาษาต่าง ๆ */}
                <div id="type-12" className="scroll-mt-28 bg-white border border-amber-200 p-6 rounded-xl hover:shadow-md transition-shadow">
                  <h3 className="text-xl font-bold text-orange-700 mb-4">
                    ๑๒. หนังสือที่พิมพ์เป็นภาษาต่าง ๆ
                  </h3>
                  <div className="text-gray-700 indent-10 leading-relaxed text-justify mb-8 space-y-4">
                    <p>
                      คือ หนังสือที่มีการจัดพิมพ์เป็นภาษาอื่น ๆ เช่น หนังสืออักษรอริยกะ
                      ภาษาเทวนาครี ภาษาพม่า ภาษาถิ่น (ล้านนา) อาทิ{" "}
                      <strong>“สาธารณสุข (ออกสุก - ปลูกสุก)”</strong> หน้าปกปรากฏข้อความว่า
                      “สาธารณสุข อันดับ ๑๙ ๑ ตุลาคม พ.ศ. ๒๔๖๖ พิเศษ
                      ไปหื้อปลูกสุกซ้ำเสียวันนี้เทอะ ได้พิมพ์เป็นอักษรลาว (มณฑลลาวเฉียง)
                      ๑๐,๐๐๐ เล่ม พิมพ์ที่โรงพิมพ์อุปโยคิน เชียงใหม่”
                    </p>
                    <p>
                      <strong>
                        “หมายประกาศเป็น “ข้อบังคับสำหรับซื้อขายช้าง”
                      </strong>{" "}
                      ตั้งไว้โดยข้าหลวงใหญ่รักษาราชการมณฑลลาวเฉียง
                      ได้รับพระราชทานพระบรมราชานุญาตแล้ว รัตนโกสินทร์ศก ๑๑๗ (พ.ศ. ๒๔๔๑)
                      พิมพ์คราวแรก ๕๐๐ ฉบับ เป็นอักษรล้านนา ภาษาล้านนา กล่าวถึง
                      หมายประกาศข้อบังคับสำหรับซื้อขายช้างที่กำหนดขึ้นและประกาศให้ทราบทั่วกันในมณฑลลาวเฉียง
                      จากเหตุการณ์บ้านเมืองในยุคสมัยนั้นเกิดโจรผู้ร้ายลอบลักช้าง
                      ข้าหลวงใหญ่รักษาราชการมณฑลลาวเฉียง
                      เห็นชอบและอนุญาตให้ประกาศหมายประกาศดังกล่าว
                      เป็นหนังสือหายากที่ทรงคุณค่าทั้งทางด้านอักษร
                      ภาษาและประวัติศาสตร์ของท้องถิ่น
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                    <figure className="flex flex-col items-center">
                      <div className="bg-white border border-amber-100 p-2 rounded-lg shadow-sm max-w-sm">
                        <div className="w-full">
                          <img src="/assets/ภาพที่27.jpg" alt="สาธารณสุข (ออกสุก-ปลูกสุก)" loading="lazy" className="w-full h-auto object-contain rounded-sm" />
                        </div>
                      </div>
                      <figcaption className="text-center text-gray-700 text-sm">
                        <strong className="block text-orange-800 mb-1">
                          ภาพที่ ๒๗ หนังสือที่พิมพ์เป็นภาษาท้องถิ่น (ล้านนา)
                        </strong>
                        <span>“สาธารณสุข (ออกสุข-ปลูกสุข)” (พ.ศ. ๒๔๖๖)</span>
                      </figcaption>
                    </figure>
                    <figure className="flex flex-col items-center">
                      <div className="bg-white border border-amber-100 p-2 rounded-lg shadow-sm max-w-sm">
                        <div className="w-full">
                          <img src="/assets/ภาพที่28.jpg" alt="หมายประกาศข้อบังคับสำหรับซื้อขายช้าง" loading="lazy" className="w-full h-auto object-contain rounded-sm" />
                        </div>
                      </div>
                      <figcaption className="text-center text-gray-700 text-sm">
                        <strong className="block text-orange-800 mb-1">
                          ภาพที่ ๒๘ หนังสือที่จัดพิมพ์เป็นภาษาท้องถิ่น (ล้านนา)
                        </strong>
                        <span>
                          “หมายประกาศเป็นข้อบังคับสำหรับซื้อขายช้าง” (พ.ศ. ๒๔๔๑)
                        </span>
                      </figcaption>
                    </figure>
                  </div>
                </div>

              </div>

              {/* ID: conclusion */}
              <section id="conclusion" className="scroll-mt-28">
                <h2 className="text-2xl font-bold text-orange-800 mt-12 mb-6 pb-2 border-b border-gray-200 flex items-center gap-2">
                  <span>🌟</span> บทสรุป
                </h2>
                <p className="mb-6 leading-relaxed indent-10">
                  หนังสือหายากที่มีอายุมากกว่า ๑๐๐ ปี ของหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่ ถือว่าเป็นเอกสาร หลักฐานสําคัญที่ใช้ในการศึกษา ค้นคว้า และอ้างอิงในด้านต่าง ๆ อัน สะท้อนให้เห็นถึงเรื่องราว ความรู้ และวิวัฒนาการของสังคมแต่ละยุคสมัย ทั้ง ด้านประวัติศาสตร์ สังคม   ประเพณี   วัฒนธรรม ภาษา วรรณกรรม การพิมพ์ ศิลปกรรม ซึ่ง   คุณค่าของหนังสือหายากเหล่านี้ไม่เพียงอยู่ที่อายุ แต่อยู่ที่เนื้อหาสาระที่ได้บันทึกไว้ตั้งแต่อดีตในแต่ละยุคสมัยที่แตกต่างกันไป นอกจากนี้จะเห็นได้ว่า หอสมุดแห่งชาติรัชมังคลาภิเษก    เชียงใหม่ ยังเป็นหอสมุดแห่งชาติสาขาแห่งเดียวประจําภาคเหนือ ที่ถือว่ามีบทบาทสําคัญในการเป็นแหล่งเรียนรู้ที่รวบรวมหนังสือหายาก   ในท้องถิ่นเป็นอย่างมาก โดยหน่วยงานได้   มุ่ง พัฒนา การให้บริการ   หนังสือหายากในรูปแบบที่สอดคล้องกับปัจจุบัน ควบคู่ไปพร้อม  กับการดําเนินการแปลงข้อมูลหนังสือหายากให้เป็นดิจิทัล เพื่อ   อนุรักษ์   ต้นฉบับ   หนังสือหายากที่มีคุณค่าเหล่านี้ ให้คงอยู่อย่างยาวนาน ถ่ายทอดองค์ความรู้อันเป็นมรดกภูมิปัญญาที่ปรากฏในหนังสือหายากให้เผยแพร่อย่างกว้างขวาง และเกิดการใช้ประโยชน์ในการศึกษา ค้นคว้าและวิจัยต่อไป
                </p>
                <p className="text-lg font-medium text-gray-800">
                  สำหรับผู้ที่สนใจค้นคว้า สามารถติดต่อสอบถามได้ที่ หอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่
                </p>
              </section>

              {/* ID: bibliography */}
              <section id="bibliography" className="scroll-mt-28">
                <footer className="mt-16 pt-8 border-t border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <span>📖</span> บรรณานุกรม
                  </h3>
                  <ul className="space-y-4 text-sm text-gray-600 leading-relaxed list-disc pl-5 font-light">
                    <li>
                      กองสาธารณสุข. <em>สาธารณสุข (ปลูกสุข – ออกสุข).</em> เชียงใหม่ฯ: โรงพิมพ์อุปโยคิน เชียงใหม่, ๒๔๖๑.
                    </li>
                    <li>
                      คณะกรรมการอํานวยการสร้างหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่. <em>ที่ระลึกในพิธีเปิดหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่.</em> กรุงเทพฯ: อมรินทร์ พริ้นติ้ง กรุ๊พ, ๒๕๓๒.
                    </li>
                    <li>
                      คณะทํางานประวัติการพิมพ์ในประเทศไทย. <em>สยามพิมพการ: ประวัติการพิมพ์ในประเทศไทย.</em> กรุงเทพฯ: มติชน, ๒๕๖๕.
                    </li>
                    <li>
                      <em>คําแนะนํา พระอธิการวัด, เจ้าคณะหมวด, เจ้าคณะแขวง และพระอุปัชฌาย์ในจังหวัดลําพูนของ พระครูญาณมงคล เจ้าคณะจังหวัดลําพูน.</em> เชียงใหม่: โรงพิมพ์อุปโยคินบํารุงประเทศเจริญ, ๒๔๖๒.
                    </li>
                    <li>
                      <em>จดหมายเหตุเสด็จประพาศต่างประเทศในรัชกาลที่ ๕ เสด็จเมืองสิงคโปร์ แลเมืองเบตาเวียครั้งแรก แลเสด็จประพาศประเทศอินเดีย.</em> ม.ป.ท.: ม.ป.พ., ๒๔๖๑.
                    </li>
                    <li>
                      จุลจอมเกล้าเจ้าอยู่หัว, พระบาทสมเด็จพระ. <em>นิทรา ชาคริช.</em> พระนคร: โรงพิมพ์อักษรนิติ์, ๒๔๕๕.
                    </li>
                    <li>
                      ________. <em>พระราชนิพนธ์เรื่องไกลบ้าน.</em> พระนคร: โรงพิมพ์โสภณพิพรรฒธนากร, ๒๔๖๖.
                    </li>
                    <li>
                      ________. <em>พระราชหัตถ์เลขาส่วนพระองค์ สมเด็จพระรามาธิบดี ศรีสินทรมหาจุฬาลงกรณ์ พระจุลจอมเกล้าเจ้าอยู่หัว ทรงมีพระราชทานแด่ สมเด็จพระศรีพัชรินทรา บรมราชินีนารถ พระบรมราชชนนี...</em> พระนคร: โรงพิมพ์โสภณพิพรรฒธนากร, ๒๔๖๓.
                    </li>
                    <li>
                      เช็กสเปียร์, วิลเลียม. <em>เรื่องตามใจท่าน.</em> พระนคร: ม.ป.พ., ๒๔๖๐.
                    </li>
                    <li>
                      เทศบาลเมืองลําพูน. <em>หอสมุดแห่งชาติหริภุญไชย เทศบาลเมืองลําพูน.</em> [ออนไลน์]. สืบค้นเมื่อ ๒๘ กรกฎาคม ๒๕๖๖, จาก: <a href="https://lamphuncity.go.th/12319/" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">https://lamphuncity.go.th/12319/</a>
                    </li>
                    <li>
                      <em>ประชุมประกาศรัชกาลที่ ๔ ภาค ๔.</em> ม.ป.ท.: โรงพิมพ์โสภณพิพรรฒธนากร, ๒๔๖๖.
                    </li>
                    <li>
                      <em>ประชุมหนังสือเก่า ภาค ๒.</em> พระนคร: โรงพิมพ์ไทย, ๒๔๕๙.
                    </li>
                    <li>
                      <em>ประวัติหลวงสันทิศวิทยาสัณห์ (เกลี้ยง สุนทรรัตน์) กับ ปกิณ์กธรรม.</em> ม.ป.ท.: ม.ป.พ., ๒๔๖๑.
                    </li>
                    <li>
                      <em>แผนที่เดินทางในบริเวณมณฑลราชบุรี มาตราส่วน ๑:๒๐๐,๐๐๐.</em> ม.ป.ท.: ม.ป.พ., ๒๔๕๘.
                    </li>
                    <li>
                      มงกุฏเกล้าเจ้าอยู่หัว, พระบาทสมเด็จพระ. <em>การสงครามป้อมค่ายประชิดปาฐะกถา.</em> พระนคร: โรงพิมพ์โสภณพิพรรฒธนากร, ๒๔๖๓.
                    </li>
                    <li>
                      ________. <em>นิราศมะเหลเถไถ.</em> พระนคร: ม.ป.พ., ๒๔๖๕.
                    </li>
                    <li>
                      ________. <em>บ่วงมาร ลครพูด ๔ ชุด.</em> ม.ป.ท.: ม.ป.พ., ๒๔๕๘.
                    </li>
                    <li>
                      ________. <em>รามกียรติ์ บทร้องและบทพากย์.</em> ม.ป.ท.: ม.ป.พ., ๒๔๖๔.
                    </li>
                    <li>
                      มัดธาย. <em>MATTHEW เรื่องประเสริฐของพระเยซูคริศต์.</em> ม.ป.ท.: โรงพิมพ์ครูอเมริกัน, ๒๔๕๓.
                    </li>
                    <li>
                      <em>ราชสกุลวงศ์ พระนามเจ้าฟ้าแลพระองค์เจ้าในกรุงรัตนโกสินทร์.</em> พระนคร: โรงพิมพ์ไท, ๒๔๖๓.
                    </li>
                    <li>
                      โรงเรียนยุพราชวิทยาลัย. <em>พระบารมีปกเกล้า ยุพราชวิทยาลัย ๑๐๐ ปี นามพระราชทาน นครเชียงใหม่ : ประวัติศาสตร์ การพัฒนา การศึกษา และสังคมนครเมืองเชียงใหม่.</em> เชียงใหม่: เชียงใหม่โรงพิมพ์แสงศิลป์, ๒๕๕๐.
                    </li>
                    <li>
                      ศิลปากรสมาคม. <em>สารานุกรมศิลปากร ฉบับปฐมฤกษ์.</em> กรุงเทพฯ: ศิลปากรสมาคม, ๒๕๕๖.
                    </li>
                    <li>
                      ศ.ร.. <em>เรื่อง เต็ลมา ภาคที่สอง เล่ม ๑.</em> กรุงเทพฯ: โรงพิมพ์กรุงเทพฯ เดลิเมล์, ๒๔๕๕.
                    </li>
                    <li>
                      <em>สาราทานปริยาย กถามรค.</em> พระนคร: โรงพิมพ์บ้านนายสิน, ๒๔๒๓.
                    </li>
                    <li>
                      <em>เสภาเรื่องพระราชพงษาวดาร กรุงเก่า.</em> ม.ป.ท.: ม.ป.พ, ๒๔๕๔.
                    </li>
                    <li>
                      สํานักหอสมุดแห่งชาติ. <em>ความรู้ด้านการอนุรักษ์หนังสือหายาก.</em> กรุงเทพฯ: สํานักหอสมุดแห่งชาติ, ๒๕๕๑.
                    </li>
                    <li>
                      ________. <em>ประชุมคํานํา คําอิบายหนังสือของหอพระสมุดวชิรญาณ สําหรับพระนคร เล่ม ๒.</em> กรุงเทพฯ: สํานักหอสมุดแห่งชาติ, ๒๕๖๕.
                    </li>
                    <li>
                      ________. <em>การสํารวจหนังสือหายากของหอสมุดแห่งชาติ : รายงานการวิจัย.</em> กรุงเทพฯ: สํานักหอสมุดแห่งชาติ, ๒๕๖๔.
                    </li>
                    <li>
                      ________. <em>สมเด็จพระเทพรัตนราชสุดาฯ สยามบรมราชกุมารีกับงานห้องสมุด.</em> กรุงเทพฯ: สํานักหอสมุดแห่งชาติ, ๒๕๕๘.
                    </li>
                    <li>
                      ________. <em>๑๐๐ ปี หอสมุดแห่งชาติ.</em> กรุงเทพฯ: สํานักหอสมุดแห่งชาติ, ๒๕๔๘.
                    </li>
                    <li>
                      หอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่. <em>ทะเบียนหนังสือหายากของหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่.</em> เชียงใหม่: หอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่, ๒๕๖๓.
                    </li>
                    <li>
                      <em>หนังสือประถม ก กา แจก ลูก.</em> กรุงเทพฯ: โรงพิมพ์สามัคคี, ๒๔๕๒.
                    </li>
                    <li>
                      <em>หนังสือธัม์มจัก์กัป์ปวัต์ตนสูตร์.</em> พระนคร: โรงพิมพ์บํารุงนุกูลกิจ, ๒๔๔๘.
                    </li>
                    <li>
                      <em>สุภาษิตบรรยาย.</em> ม.ป.ท.: โรงพิมพ์บํารุงนุกูลกิจ, ๒๔๖๑.
                    </li>
                    <li>
                      <em>หมายประกาศเป็นข้อบังคับสําหรับซื้อขายช้าง.</em> ม.ป.ท.: ม.ป.พ., ๒๔๔๑.
                    </li>
                    <li>
                      De Choisy, M. L'Abbe. <em>Journal Du Voyage De Siam Fait En 1685 & 1686.</em> Paris: Editions Duchartre et Van Buggenhoudt, 1910.
                    </li>
                  </ul>
                </footer>
              </section>

              {/* ปุ่มกลับด้านบน Mobile */}
              <div className="mt-8 lg:hidden">
                <BackToTop />
              </div>

            </article>
          </main>

          {/* === ส่วนสารบัญ Sidebar (4 Columns) === */}
          <aside className="hidden lg:block lg:col-span-4">
            <div className="sticky top-24">
              <nav className="bg-white rounded-2xl shadow-sm ring-1 ring-black/5 p-6 max-h-[calc(100vh-8rem)] overflow-y-auto custom-scrollbar">
                <h2 className="text-lg font-semibold text-orange-900 mb-4 pb-2 border-b border-orange-100">
                  สารบัญ
                </h2>
                <div className="space-y-1">
                  {SECTIONS.map((s) => (
                    <Anchor key={s.id} id={s.id}>
                      {s.title}
                    </Anchor>
                  ))}
                </div>
                {/* ปุ่ม Prev/Next ใน Sidebar */}
                <PrevNext sections={SECTIONS} active={active} />
              </nav>

              <div className="mt-6 flex justify-center">
                <BackToTop />
              </div>
            </div>
          </aside>

        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RareBooksArticlePage;